import React, { useState, useEffect } from 'react'
import type { Language, LocalizationStrings } from '../../../config/localization'

// Exported interfaces for reuse
export interface SchemaField {
  field_id: string
  label: string
  field_type: string
  required: boolean
  widget_name: string
  config: Record<string, any>
}

export interface MediaConfig {
  maxCount: number | null
  maxFileSize?: number | null
}

export interface WorkflowSchema {
  workflowId: string
  visual_id: string
  title: string
  fields: SchemaField[]
  has_file_upload: boolean
  is_pipeline?: boolean
  media_inputs?: {
    image_urls?: MediaConfig
    video_urls?: MediaConfig
    audio_urls?: MediaConfig
  }
}

// Fields to hide from curl examples (auto-set on backend)
export const HIDDEN_CURL_FIELDS = ['gen_count', 'num_images']

// Generate example value based on field type
export function generateExampleValue(field: SchemaField): any {
  switch (field.field_type) {
    case 'string_field':
      if (field.field_id === 'prompt') {
        return 'a beautiful sunset over mountains, photorealistic'
      }
      return `example ${field.label.toLowerCase()}`

    case 'int_field':
      if (field.field_id === 'seed') {
        return 12345
      }
      // Support both min/max and min_value/max_value formats
      return field.config?.default_value ?? field.config?.min_value ?? field.config?.min ?? 0

    case 'slider':
      // Support both min/max and min_value/max_value formats from DB
      const min = field.config?.min_value ?? field.config?.min ?? 0
      const max = field.config?.max_value ?? field.config?.max ?? 100
      const defaultValue = field.config?.default_value
      if (defaultValue !== undefined) {
        return defaultValue
      }
      return Math.round((min + max) / 2 * 100) / 100

    case 'dropdown':
      // Options can be string (newline-separated) or array
      const options = field.config?.options
      if (typeof options === 'string') {
        // Parse newline-separated options like "21:9\n16:9\n4:3\n..."
        const optionList = options.split('\n').filter(Boolean)
        // Find default or use first option
        const defaultOpt = field.config?.default_value
        return defaultOpt || optionList[0] || ''
      }
      return options?.[0]?.value ?? field.config?.default_value ?? ''

    case 'dropdown_prompt': {
      const dpOptions = field.config?.options
      if (Array.isArray(dpOptions) && dpOptions.length > 0) {
        return field.config?.default_value || String(dpOptions[0].prompt_id) || ''
      }
      return field.config?.default_value || ''
    }

    case 'multiselect':
      const msOptions = field.config?.options
      if (typeof msOptions === 'string') {
        const optList = msOptions.split('\n').filter(Boolean)
        return optList.length > 0 ? [optList[0]] : []
      }
      const firstOption = msOptions?.[0]?.value
      return firstOption ? [firstOption] : []

    case 'image_size':
      return {
        width: field.config?.defaultWidth ?? field.config?.default_width ?? 1024,
        height: field.config?.defaultHeight ?? field.config?.default_height ?? 1024
      }

    default:
      return null
  }
}

// Generate example inputs object from schema fields and media inputs
export function generateExampleInputs(fields: SchemaField[], mediaInputs?: WorkflowSchema['media_inputs']): Record<string, any> {
  const inputs: Record<string, any> = {}

  for (const field of fields) {
    // Skip hidden fields - they have default values on backend
    if (HIDDEN_CURL_FIELDS.includes(field.field_id)) {
      continue
    }
    const value = generateExampleValue(field)
    if (value !== null) {
      inputs[field.field_id] = value
    }
  }

  // Add media URL examples based on config
  if (mediaInputs) {
    if (mediaInputs.image_urls) {
      inputs.image_urls = ['https://example.com/image1.jpg']
    }
    if (mediaInputs.video_urls) {
      inputs.video_urls = ['https://example.com/video1.mp4']
    }
    if (mediaInputs.audio_urls) {
      inputs.audio_urls = ['https://example.com/audio1.mp3']
    }
  }

  return inputs
}

interface CurlExamplesContentProps {
  workflowId: string
  language: Language
  t: LocalizationStrings
  createAuthHeaders: () => Promise<Record<string, string>>
  onAllTextReady?: (text: string) => void
}

const CurlExamplesContent: React.FC<CurlExamplesContentProps> = ({
  workflowId,
  language,
  t,
  createAuthHeaders,
  onAllTextReady
}) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const [schema, setSchema] = useState<WorkflowSchema | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const baseUrl = window.location.origin

  // Load workflow schema on mount
  useEffect(() => {
    const loadSchema = async () => {
      try {
        setLoading(true)
        setError(null)

        const headers = await createAuthHeaders()
        const response = await fetch(`/api/admin/workflows/${workflowId.trim()}/schema`, { headers })

        if (!response.ok) {
          throw new Error('Failed to load workflow schema')
        }

        const data = await response.json()
        setSchema(data)
      } catch (err) {
        console.error('Error loading schema:', err)
        setError(err instanceof Error ? err.message : 'Failed to load schema')
      } finally {
        setLoading(false)
      }
    }

    loadSchema()
  }, [workflowId, createAuthHeaders])

  const handleCopy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedIndex(index)
      setTimeout(() => setCopiedIndex(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // Get user-friendly type name
  const getDisplayType = (field: SchemaField): string => {
    const config = field.config
    switch (field.field_type) {
      case 'string_field':
        return 'string'
      case 'int_field':
        return 'integer'
      case 'slider': {
        // Check if step has decimals - then it's float
        const step = config?.step
        if (step && step !== Math.floor(step)) {
          return 'float'
        }
        return 'integer'
      }
      case 'dropdown':
      case 'dropdown_prompt':
        return 'enum'
      case 'multiselect':
        return 'array'
      case 'image_size':
        return 'object'
      case 'boolean':
        return 'boolean'
      default:
        return field.field_type
    }
  }

  // Format field details based on type
  const getFieldDetails = (field: SchemaField): string | null => {
    const config = field.config
    switch (field.field_type) {
      case 'slider':
      case 'int_field': {
        const min = config?.min_value ?? config?.min
        const max = config?.max_value ?? config?.max
        const step = config?.step
        const def = config?.default_value
        const parts = []
        if (min !== undefined && max !== undefined) {
          parts.push(`Range: ${min}...${max}`)
        }
        if (step && step !== 1) {
          parts.push(`step: ${step}`)
        }
        if (def !== undefined) {
          parts.push(`Default value: ${def}`)
        }
        return parts.length > 0 ? parts.join('. ') : null
      }
      case 'dropdown': {
        const options = config?.options
        let optList: string[] = []
        if (typeof options === 'string') {
          optList = options.split('\n').filter(Boolean)
        } else if (Array.isArray(options)) {
          optList = options.map(o => o.value || o)
        }
        if (optList.length > 0) {
          return `Possible enum values: ${optList.join(', ')}`
        }
        return null
      }
      case 'dropdown_prompt': {
        const dpOptions = config?.options
        if (Array.isArray(dpOptions) && dpOptions.length > 0) {
          const optList = dpOptions
            .filter((o: any) => o.label && o.prompt_id)
            .map((o: any) => `${o.label} (${o.prompt_id})`)
          if (optList.length > 0) {
            return `Possible enum values: ${optList.join(', ')}`
          }
        }
        return null
      }
      case 'multiselect': {
        const msOptions = config?.options
        let optList: string[] = []
        if (typeof msOptions === 'string') {
          optList = msOptions.split('\n').filter(Boolean)
        } else if (Array.isArray(msOptions)) {
          optList = msOptions.map(o => o.value || o)
        }
        if (optList.length > 0) {
          return `Possible values: ${optList.join(', ')}`
        }
        return null
      }
      case 'string_field': {
        const maxLen = config?.maxLength || config?.max_length
        return maxLen ? `Max length: ${maxLen}` : null
      }
      case 'image_size': {
        const defWidth = config?.defaultWidth ?? config?.default_width ?? 1024
        const defHeight = config?.defaultHeight ?? config?.default_height ?? 1024
        return `Object with width and height. Default value: { "width": ${defWidth}, "height": ${defHeight} }`
      }
      default:
        return null
    }
  }

  // Detect pipeline mode from schema
  const isPipeline = schema?.is_pipeline ?? false
  const apiVersion = 'v2'

  // Generate dynamic inputs based on schema
  const exampleInputs = schema ? generateExampleInputs(schema.fields, schema.media_inputs) : { prompt: 'a beautiful sunset over mountains', seed: 12345 }
  const inputsJson = JSON.stringify(exampleInputs, null, 6).split('\n').map((line, i) => i === 0 ? line : '      ' + line).join('\n')

  // Generate parameters text for Copy
  const parametersText = schema ? (() => {
    const fieldsText = schema.fields
      .filter(field => !HIDDEN_CURL_FIELDS.includes(field.field_id))
      .map((field) => {
        const displayType = getDisplayType(field)
        const details = getFieldDetails(field)
        let line = `${field.field_id}  ${displayType}`
        if (field.label && field.label !== field.field_id) {
          line += `\n${field.label}.`
        }
        if (details) {
          line += `\n${details}`
        }
        return line
      }).join('\n\n')

    let mediaText = ''
    if (schema.media_inputs && Object.keys(schema.media_inputs).length > 0) {
      const mediaLines = []
      if (schema.media_inputs.image_urls) {
        const imgConfig = schema.media_inputs.image_urls
        let desc = 'Array of image URLs.'
        if (imgConfig.maxCount) desc += ` Max: ${imgConfig.maxCount} images.`
        if (imgConfig.maxFileSize) desc += ` Max size: ${imgConfig.maxFileSize}MB.`
        mediaLines.push(`image_urls  string[]\n${desc}`)
      }
      if (schema.media_inputs.video_urls) {
        const vidConfig = schema.media_inputs.video_urls
        let desc = 'Array of video URLs.'
        if (vidConfig.maxCount) desc += ` Max: ${vidConfig.maxCount} videos.`
        if (vidConfig.maxFileSize) desc += ` Max size: ${vidConfig.maxFileSize}MB.`
        mediaLines.push(`video_urls  string[]\n${desc}`)
      }
      if (schema.media_inputs.audio_urls) {
        const audConfig = schema.media_inputs.audio_urls
        let desc = 'Array of audio URLs.'
        if (audConfig.maxCount) desc += ` Max: ${audConfig.maxCount} files.`
        if (audConfig.maxFileSize) desc += ` Max size: ${audConfig.maxFileSize}MB.`
        mediaLines.push(`audio_urls  string[]\n${desc}`)
      }
      if (mediaLines.length > 0) {
        mediaText = '\n\n# Media Inputs\n\n' + mediaLines.join('\n\n')
      }
    }

    return fieldsText + mediaText
  })() : ''

  const examples = [
    {
      title: t.curlExamplesModal?.executeTitle || 'Execute Workflow',
      description: t.curlExamplesModal?.executeDescription || 'Start a new generation with this workflow',
      curl: `curl -X POST "${baseUrl}/api/${apiVersion}/workflow/execute" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "workflowId": "${schema?.visual_id || workflowId}",
    "inputs": ${inputsJson}
  }'`,
      responses: [
        {
          status: 200,
          statusText: 'OK',
          label: 'Success',
          color: 'green',
          body: isPipeline
            ? `{
  "generationId": 12345,
  "status": "pending",
  "tokensCost": 10,
  "checkStatusUrl": "/api/v2/generations/12345/check",
  "executionTime": 45,
  "async": true,
  "pipeline": {
    "currentStep": 0,
    "totalSteps": 3,
    "steps": [
      { "name": "Step 1", "status": "running" },
      { "name": "Step 2", "status": "pending" },
      { "name": "Step 3", "status": "pending" }
    ]
  }
}`
            : `{
  "generationId": 12345,
  "status": "pending",
  "tokensCost": 10,
  "checkStatusUrl": "/api/v2/generations/12345/check",
  "executionTime": 922,
  "async": true,
  "jobId": "dc1a31d4-8a57-4443-ad31-0c1d3ea34302"
}`
        },
        {
          status: 400,
          statusText: 'Bad Request',
          label: 'Validation Error',
          color: 'red',
          body: `{
  "error": "Validation error",
  "message": "Missing required fields: inputs.prompt",
  "details": [
    {
      "field": "inputs.prompt",
      "message": "Required",
      "expected": "string",
      "received": "undefined"
    }
  ]
}`
        },
        {
          status: 401,
          statusText: 'Unauthorized',
          label: 'Auth Error',
          color: 'red',
          body: `{
  "error": "Invalid API key",
  "details": "The provided API key is invalid or expired"
}`
        },
        {
          status: 402,
          statusText: 'Payment Required',
          label: 'Insufficient Tokens',
          color: 'yellow',
          body: `{
  "error": "Insufficient tokens",
  "details": "Insufficient tokens. Required: 10, Available: 5",
  "required": 10
}`
        },
        {
          status: 429,
          statusText: 'Too Many Requests',
          label: 'Rate Limit Exceeded',
          color: 'yellow',
          body: `{
  "error": "Rate limit exceeded",
  "retryAfter": 30
}

Headers:
  Retry-After: 30
  X-RateLimit-Limit-Minute: 60
  X-RateLimit-Remaining-Minute: 0
  X-RateLimit-Limit-Hour: 1000
  X-RateLimit-Remaining-Hour: 950`
        },
        {
          status: 500,
          statusText: 'Internal Server Error',
          label: 'Server Error',
          color: 'red',
          body: `{
  "error": "Internal server error",
  "details": "An unexpected error occurred. Please try again later."
}`
        },
        {
          status: 503,
          statusText: 'Service Unavailable',
          label: 'Service Unavailable',
          color: 'red',
          body: `{
  "error": "Service temporarily unavailable",
  "details": "The generation service is currently unavailable. Please try again later."
}`
        }
      ]
    },
    {
      title: t.curlExamplesModal?.checkTitle || 'Check Generation Status',
      description: t.curlExamplesModal?.checkDescription || 'Actively check and update the status of an async generation. Call this endpoint to poll for completion.',
      curl: `# Use generationId from execute response
curl -X POST "${baseUrl}/api/${apiVersion}/generations/12345/check" \\
  -H "X-API-Key: YOUR_API_KEY"`,
      responses: [
        {
          status: 200,
          statusText: 'OK',
          label: 'Completed',
          color: 'green',
          body: isPipeline
            ? `{
  "generationId": 12345,
  "status": "completed",
  "completed": true,
  "result": {
    "previewUrls": ["https://content.example.com/preview/abc123.png"],
    "originalUrls": ["https://content.example.com/original/abc123.png"],
    "contentTypes": ["image/png"]
  },
  "contentType": "image",
  "pipeline": {
    "currentStep": 3,
    "totalSteps": 3,
    "steps": [
      { "name": "Step 1", "status": "completed" },
      { "name": "Step 2", "status": "completed" },
      { "name": "Step 3", "status": "completed" }
    ]
  }
}`
            : `{
  "generationId": 12345,
  "status": "completed",
  "completed": true,
  "message": "Job completed successfully",
  "result": {
    "previewUrls": ["https://content.example.com/preview/abc123.png"],
    "originalUrls": ["https://content.example.com/original/abc123.png"],
    "externalUrls": [],
    "contentTypes": ["image/png"]
  },
  "contentType": "image"
}`
        },
        {
          status: 200,
          statusText: 'OK',
          label: 'In Progress',
          color: 'yellow',
          body: isPipeline
            ? `{
  "generationId": 12345,
  "status": "checking",
  "completed": false,
  "pipeline": {
    "currentStep": 1,
    "totalSteps": 3,
    "steps": [
      { "name": "Step 1", "status": "completed" },
      { "name": "Step 2", "status": "running" },
      { "name": "Step 3", "status": "pending" }
    ]
  }
}`
            : `{
  "generationId": 12345,
  "status": "checking",
  "completed": false,
  "jobState": "IN_PROGRESS",
  "message": "Job is still processing"
}`
        },
        {
          status: 200,
          statusText: 'OK',
          label: 'Failed Generation',
          color: 'red',
          body: `{
  "generationId": 12345,
  "status": "failed",
  "completed": true,
  "error": "NSFW content detected",
  "errorType": "content_policy"
}`
        },
        {
          status: 500,
          statusText: 'Internal Server Error',
          label: 'Provider Reported Failure',
          color: 'red',
          body: `{
  "generationId": 12345,
  "status": "failed",
  "completed": true,
  "error": "Provider failed",
  "errorType": "provider_error"
}

# Returned by the check that first observes the provider failure;
# "completed": true means it is final. Later checks answer 200 (Failed Generation).`
        },
        {
          status: 401,
          statusText: 'Unauthorized',
          label: 'Auth Error',
          color: 'red',
          body: `{
  "error": "Invalid API key",
  "details": "The provided API key is invalid or expired"
}`
        },
        {
          status: 404,
          statusText: 'Not Found',
          label: 'Not Found',
          color: 'red',
          body: `{
  "error": "Generation not found",
  "details": "No generation found with the specified ID"
}`
        },
        {
          status: 500,
          statusText: 'Internal Server Error',
          label: 'Server Error',
          color: 'red',
          body: `{
  "error": "Internal server error",
  "details": "An unexpected error occurred. Please try again later."
}`
        },
        {
          status: 503,
          statusText: 'Service Unavailable',
          label: 'Service Unavailable',
          color: 'red',
          body: `{
  "error": "Service temporarily unavailable",
  "details": "The generation service is currently unavailable. Please try again later."
}`
        }
      ]
    }
  ]

  // Generate all text for Copy All functionality
  useEffect(() => {
    if (!loading && schema && onAllTextReady) {
      const header = `# Workflow: ${schema.visual_id || workflowId}${schema.title ? ` (${schema.title})` : ''}\n# Base URL: ${baseUrl}\n\n`

      const paramsSection = parametersText
        ? `# AVAILABLE PARAMETERS\n${parametersText}\n\n${'='.repeat(80)}\n\n`
        : ''

      const examplesText = examples.map(example => {
        let text = `# ${example.title}\n`
        text += `# ${example.description}\n\n`
        text += `# REQUEST\n${example.curl}\n\n`
        text += example.responses.map(resp =>
          `# ${resp.status} ${resp.statusText} - ${resp.label}\n${resp.body}`
        ).join('\n\n')
        return text
      }).join('\n\n' + '='.repeat(80) + '\n\n')

      onAllTextReady(header + paramsSection + examplesText)
    }
  }, [loading, schema, examples, onAllTextReady, workflowId, baseUrl, parametersText])

  return (
    <div className="space-y-8">
      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-400">
            {t.curlExamplesModal?.loading || 'Loading schema...'}
          </span>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="bg-red-900/30 border border-red-600 rounded-lg p-4 text-red-300">
          {error}
        </div>
      )}

      {/* Workflow info header */}
      {schema && !loading && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <p className="text-sm text-gray-400">
              Workflow: <span className="text-blue-400 font-mono">{schema.visual_id || workflowId}</span>
              {schema.title && <span className="text-gray-500 ml-2">({schema.title})</span>}
            </p>
            {isPipeline && (
              <span className="px-2 py-0.5 text-xs font-medium bg-purple-600/30 text-purple-300 border border-purple-500/40 rounded">
                {t.curlExamplesModal?.pipelineBadge || 'Pipeline — API v2'}
              </span>
            )}
          </div>
          {isPipeline && (
            <p className="text-xs text-purple-300/70 mt-2">
              {t.curlExamplesModal?.pipelineNote || 'This workflow uses a multi-step pipeline and requires API v2 endpoints.'}
            </p>
          )}
        </div>
      )}

      {/* Rate Limiting Info */}
      {schema && !loading && (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-white">
              {t.curlExamplesModal?.rateLimiting || 'Rate Limiting'}
            </h3>
            <p className="text-sm text-gray-400">
              {t.curlExamplesModal?.rateLimitingDesc || 'API requests are rate limited per API key'}
            </p>
          </div>
          <div className="bg-gray-950 border border-gray-800 rounded-lg p-4 space-y-3">
            <div className="font-mono text-sm">
              <p className="text-gray-400 mb-2">
                {t.curlExamplesModal?.rateLimitNote || 'Rate limits are configured per API key. All successful responses include rate limit headers, except status checks (/generations/:id/check), which are not rate limited:'}
              </p>
              <div className="space-y-1 text-xs">
                <div className="flex gap-4">
                  <span className="text-blue-400 font-semibold min-w-[220px]">X-RateLimit-Limit-Minute</span>
                  <span className="text-gray-400">Maximum requests per minute</span>
                </div>
                <div className="flex gap-4">
                  <span className="text-blue-400 font-semibold min-w-[220px]">X-RateLimit-Remaining-Minute</span>
                  <span className="text-gray-400">Remaining requests this minute</span>
                </div>
                <div className="flex gap-4">
                  <span className="text-blue-400 font-semibold min-w-[220px]">X-RateLimit-Limit-Hour</span>
                  <span className="text-gray-400">Maximum requests per hour</span>
                </div>
                <div className="flex gap-4">
                  <span className="text-blue-400 font-semibold min-w-[220px]">X-RateLimit-Remaining-Hour</span>
                  <span className="text-gray-400">Remaining requests this hour</span>
                </div>
              </div>
              <p className="text-yellow-400/80 mt-3 text-xs">
                {t.curlExamplesModal?.rateLimitExceeded || 'When rate limit is exceeded, you will receive a 429 response with Retry-After header indicating seconds to wait.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Available Parameters */}
      {schema && !loading && schema.fields.length > 0 && (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-white">
              {t.curlExamplesModal?.availableParams || 'Available Parameters'}
            </h3>
            <p className="text-sm text-gray-400">
              {t.curlExamplesModal?.availableParamsDesc || 'Parameters accepted by this workflow'}
            </p>
          </div>

          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 uppercase">
                {t.curlExamplesModal?.parameters || 'Parameters'}
              </span>
              <button
                type="button"
                onClick={() => handleCopy(parametersText, -1)}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  copiedIndex === -1
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                {copiedIndex === -1
                  ? (t.curlExamplesModal?.copied || 'Copied!')
                  : (t.curlExamplesModal?.copy || 'Copy')}
              </button>
            </div>
            <div className="bg-gray-950 border border-gray-800 rounded-lg p-4 space-y-4 overflow-x-auto">
              {schema.fields
                .filter(field => !HIDDEN_CURL_FIELDS.includes(field.field_id))
                .map((field) => {
                  const displayType = getDisplayType(field)
                  const details = getFieldDetails(field)
                  const description = field.config?.description || field.label

                  return (
                    <div key={field.field_id} className="font-mono text-sm">
                      <div className="flex items-baseline gap-2">
                        <span className="text-blue-400 font-semibold">{field.field_id}</span>
                        <span className="text-yellow-500">{displayType}</span>
                      </div>
                      {description && (
                        <p className="text-gray-400 mt-1 ml-0">{description}</p>
                      )}
                      {details && (
                        <p className="text-green-400/80 mt-1 ml-0">{details}</p>
                      )}
                    </div>
                  )
                })}

              {/* Media Inputs */}
              {schema.media_inputs && Object.keys(schema.media_inputs).length > 0 && (
                <div className="pt-4 border-t border-gray-800">
                  <h5 className="text-xs font-medium text-gray-500 uppercase mb-3">Media Inputs</h5>
                  <div className="space-y-4">
                    {schema.media_inputs.image_urls && (
                      <div className="font-mono text-sm">
                        <div className="flex items-baseline gap-2">
                          <span className="text-blue-400 font-semibold">image_urls</span>
                          <span className="text-yellow-500">string[]</span>
                        </div>
                        <p className="text-gray-400 mt-1">Array of image URLs to use as input.</p>
                        <p className="text-green-400/80 mt-1">
                          {schema.media_inputs.image_urls.maxCount ? `Max: ${schema.media_inputs.image_urls.maxCount} images. ` : ''}
                          {schema.media_inputs.image_urls.maxFileSize ? `Max size: ${schema.media_inputs.image_urls.maxFileSize}MB.` : ''}
                        </p>
                      </div>
                    )}
                    {schema.media_inputs.video_urls && (
                      <div className="font-mono text-sm">
                        <div className="flex items-baseline gap-2">
                          <span className="text-blue-400 font-semibold">video_urls</span>
                          <span className="text-yellow-500">string[]</span>
                        </div>
                        <p className="text-gray-400 mt-1">Array of video URLs to use as input.</p>
                        <p className="text-green-400/80 mt-1">
                          {schema.media_inputs.video_urls.maxCount ? `Max: ${schema.media_inputs.video_urls.maxCount} videos. ` : ''}
                          {schema.media_inputs.video_urls.maxFileSize ? `Max size: ${schema.media_inputs.video_urls.maxFileSize}MB.` : ''}
                        </p>
                      </div>
                    )}
                    {schema.media_inputs.audio_urls && (
                      <div className="font-mono text-sm">
                        <div className="flex items-baseline gap-2">
                          <span className="text-blue-400 font-semibold">audio_urls</span>
                          <span className="text-yellow-500">string[]</span>
                        </div>
                        <p className="text-gray-400 mt-1">Array of audio URLs to use as input.</p>
                        <p className="text-green-400/80 mt-1">
                          {schema.media_inputs.audio_urls.maxCount ? `Max: ${schema.media_inputs.audio_urls.maxCount} files. ` : ''}
                          {schema.media_inputs.audio_urls.maxFileSize ? `Max size: ${schema.media_inputs.audio_urls.maxFileSize}MB.` : ''}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Examples */}
      {!loading && examples.map((example, exampleIndex) => (
        <div key={exampleIndex} className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-white">{example.title}</h3>
            <p className="text-sm text-gray-400">{example.description}</p>
          </div>

          {/* cURL command */}
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 uppercase">
                {t.curlExamplesModal?.request || 'Request'}
              </span>
              <button
                type="button"
                onClick={() => handleCopy(example.curl, exampleIndex * 10)}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  copiedIndex === exampleIndex * 10
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                {copiedIndex === exampleIndex * 10
                  ? (t.curlExamplesModal?.copied || 'Copied!')
                  : (t.curlExamplesModal?.copy || 'Copy')}
              </button>
            </div>
            <pre className="bg-gray-950 border border-gray-800 rounded-lg p-4 text-sm text-green-400 overflow-x-auto font-mono whitespace-pre">
              {example.curl}
            </pre>
          </div>

          {/* Responses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {example.responses.map((response, respIndex) => {
              const colorClasses = {
                green: { dot: 'bg-green-500', text: 'text-green-500', status: 'text-green-400' },
                yellow: { dot: 'bg-yellow-500', text: 'text-yellow-500', status: 'text-yellow-400' },
                red: { dot: 'bg-red-500', text: 'text-red-500', status: 'text-red-400' }
              }
              const colors = colorClasses[response.color as keyof typeof colorClasses] || colorClasses.red
              const copyIndex = exampleIndex * 100 + respIndex + 1

              return (
                <div key={respIndex} className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-medium ${colors.text} uppercase flex items-center gap-2`}>
                      <span className={`w-2 h-2 ${colors.dot} rounded-full`}></span>
                      <span className={colors.status}>{response.status}</span>
                      <span className="text-gray-500">{response.statusText}</span>
                      <span className="text-gray-400">— {response.label}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopy(response.body, copyIndex)}
                      className={`px-2 py-0.5 rounded text-xs transition-colors ${
                        copiedIndex === copyIndex
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      }`}
                    >
                      {copiedIndex === copyIndex ? '✓' : (t.curlExamplesModal?.copy || 'Copy')}
                    </button>
                  </div>
                  <pre className="bg-gray-950 border border-gray-800 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto font-mono whitespace-pre">
                    {response.body}
                  </pre>
                </div>
              )
            })}
          </div>

          {exampleIndex < examples.length - 1 && (
            <div className="border-t border-gray-700 pt-4"></div>
          )}
        </div>
      ))}
    </div>
  )
}

export default CurlExamplesContent
